import { useState, useEffect } from 'react'
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../config/firebase'

export interface ClientLogo {
  id?: string
  name: string
  url: string
  order: number
}

const CLIENT_LOGOS_COLLECTION = 'clientLogos'
const CACHE_KEY = 'forax_client_logos_cache'

// Logos por defecto (solo si no hay nada en caché ni Firebase)
const DEFAULT_LOGOS: Omit<ClientLogo, 'id'>[] = [
  {
    name: 'Skechers',
    url: 'https://cdn.magicpatterns.com/uploads/6RvEuXSZWbxWikwYgYQiSC/SKECHERS.jpg',
    order: 0,
  },
  {
    name: 'BCI',
    url: 'https://cdn.magicpatterns.com/uploads/oxMqUi6hwqTvubu3jRZ43Q/BCI.png',
    order: 1,
  },
]

export function useClientLogos() {
  // Cargar desde caché inmediatamente
  const getCachedLogos = (): ClientLogo[] => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      return cached ? JSON.parse(cached) : []
    } catch {
      return []
    }
  }

  const [logos, setLogos] = useState<ClientLogo[]>(getCachedLogos())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadLogos()
  }, [])

  const loadLogos = async () => {
    try {
      setLoading(true)
      setError(null)

      const logosRef = collection(db, CLIENT_LOGOS_COLLECTION)
      const q = query(logosRef, orderBy('order', 'asc'))
      const querySnapshot = await getDocs(q)

      const loadedLogos: ClientLogo[] = []
      querySnapshot.forEach((doc) => {
        loadedLogos.push({ id: doc.id, ...doc.data() } as ClientLogo)
      })

      // Guardar en caché
      if (loadedLogos.length > 0) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(loadedLogos))
        setLogos(loadedLogos)
      } else if (logos.length === 0) {
        // Si no hay logos, usar defaults
        setLogos(DEFAULT_LOGOS as ClientLogo[])
      }

      setError(null)
    } catch (err: any) {
      console.error('Error loading client logos:', err)

      let errorMessage = 'Error al cargar logos.'

      if (err.message?.includes('Timeout')) {
        errorMessage = 'Firebase no responde. ¿Activaste Firestore Database?'
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Permisos denegados en Firestore.'
      } else {
        errorMessage = `Error: ${err.message || 'Problema de conexión'}`
      }

      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const addLogo = async (logoData: Omit<ClientLogo, 'id' | 'order'>) => {
    try {
      const newOrder = logos.length
      const docRef = await addDoc(collection(db, CLIENT_LOGOS_COLLECTION), {
        ...logoData,
        order: newOrder,
      })
      const newLogos = [
        ...logos,
        { id: docRef.id, ...logoData, order: newOrder },
      ]
      setLogos(newLogos)
      localStorage.setItem(CACHE_KEY, JSON.stringify(newLogos))
    } catch (err) {
      console.error('Error adding logo:', err)
      alert('Error al agregar logo. Verifica tu conexión.')
    }
  }

  const deleteLogo = async (id: string) => {
    try {
      await deleteDoc(doc(db, CLIENT_LOGOS_COLLECTION, id))
      const filtered = logos.filter((logo) => logo.id !== id)

      // Reordenar logos restantes
      for (let i = 0; i < filtered.length; i++) {
        const logo = filtered[i]
        if (logo.id && logo.order !== i) {
          await updateDoc(doc(db, CLIENT_LOGOS_COLLECTION, logo.id), {
            order: i,
          })
          filtered[i] = { ...logo, order: i }
        }
      }

      setLogos(filtered)
      localStorage.setItem(CACHE_KEY, JSON.stringify(filtered))
    } catch (err) {
      console.error('Error deleting logo:', err)
      alert('Error al eliminar logo. Verifica tu conexión.')
    }
  }

  const deleteAllLogos = async () => {
    try {
      const batch = writeBatch(db)
      logos.forEach((logo) => {
        if (logo.id) {
          const logoRef = doc(db, CLIENT_LOGOS_COLLECTION, logo.id)
          batch.delete(logoRef)
        }
      })
      await batch.commit()

      setLogos([])
      localStorage.removeItem(CACHE_KEY)

      alert('Todos los logos han sido eliminados exitosamente.')
    } catch (err) {
      console.error('Error deleting all logos:', err)
      alert('Error al eliminar todos los logos. Verifica tu conexión.')
    }
  }

  const moveLogo = async (id: string, direction: 'up' | 'down') => {
    const index = logos.findIndex((logo) => logo.id === id)
    if (index === -1) return
    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === logos.length - 1) return

    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const newLogos = [...logos]

    // Intercambiar
    const temp = newLogos[index]
    newLogos[index] = newLogos[targetIndex]
    newLogos[targetIndex] = temp

    // Actualizar orden en Firebase
    try {
      if (newLogos[index].id) {
        await updateDoc(doc(db, CLIENT_LOGOS_COLLECTION, newLogos[index].id!), {
          order: index,
        })
      }
      if (newLogos[targetIndex].id) {
        await updateDoc(
          doc(db, CLIENT_LOGOS_COLLECTION, newLogos[targetIndex].id!),
          {
            order: targetIndex,
          },
        )
      }

      // Actualizar orden local
      newLogos[index] = { ...newLogos[index], order: index }
      newLogos[targetIndex] = { ...newLogos[targetIndex], order: targetIndex }

      setLogos(newLogos)
      localStorage.setItem(CACHE_KEY, JSON.stringify(newLogos))
    } catch (err) {
      console.error('Error moving logo:', err)
      alert('Error al reordenar logo. Verifica tu conexión.')
    }
  }

  const updateLogo = async (
    id: string,
    updates: Partial<Omit<ClientLogo, 'id' | 'order'>>,
  ) => {
    try {
      await updateDoc(doc(db, CLIENT_LOGOS_COLLECTION, id), updates)
      const updatedLogos = logos.map((logo) =>
        logo.id === id ? { ...logo, ...updates } : logo,
      )
      setLogos(updatedLogos)
      localStorage.setItem(CACHE_KEY, JSON.stringify(updatedLogos))
    } catch (err) {
      console.error('Error updating logo:', err)
      alert('Error al actualizar logo. Verifica tu conexión.')
    }
  }

  return {
    logos,
    loading,
    error,
    addLogo,
    deleteLogo,
    deleteAllLogos,
    moveLogo,
    updateLogo,
  }
}
